<template>
  <div>
    <user-details ref="userDetails" :can-change-role="parentUserIsCarrier">
      <div slot="cropper">
        <photo-cropper
          v-if="loggedInUserIsAdmin && accountUser.driver_profile"
          v-model="photo"
          name="driver"
          :photo-storage-path="accountUser.driver_profile.photo_storage_path"
          :original-name="accountUser.driver_profile.photo_name"
          :original-data-uri="originalPhotoDataUri"
          class-name="profile-photo"
          label-text="Profile Photo">
        </photo-cropper>

        <div v-else-if="accountUser.driver_profile && accountUser.driver_profile.photo_storage_path" class="preview">
          <img class="profile-photo" :src="`${accountUser.driver_profile.photo_storage_path}/256x256.jpg`">
        </div>
      </div>
    </user-details>

    <div class="ibox" v-if="profile">
      <div class="ibox-content">
        <form class="form-material">
          <h3>Notification Settings</h3>

          <div class="margin-20"></div>

          <div class="form-group m-b-lg">
            <div class="checkbox checkbox-primary">
              <input id="check-notify-email" class="styled" type="checkbox" v-model="profile.notify_job_status">
              <label for="check-notify-email">
                Notify job status updates
              </label>
            </div>
            <div class="checkbox checkbox-primary">
              <input id="check-notify-email-delivery" class="styled" type="checkbox" v-model="profile.notify_load_deliveries">
              <label for="check-notify-email-delivery">
                Notify load deliveries
              </label>
            </div>
            <div class="checkbox checkbox-primary">
              <input id="check-notify-job-completed" class="styled" type="checkbox" v-model="profile.notify_job_completed">
              <label for="check-notify-job-completed">
                Notify invoices and payments
              </label>
            </div>
            <div class="checkbox checkbox-primary">
              <input id="notify-public-jobs" v-model="profile.notify_public_jobs" type="checkbox" class="styled"/>
              <label for="notify-public-jobs">
                Receive email about public jobs
              </label>
            </div>
          </div><!-- /.form-group -->
        </form>
      </div>
    </div>

    <div class="ibox" v-if="editingOrCreateChildAdminAccount">
      <div class="ibox-content">
        <h3>Admin System Access</h3>
        <div class="checkbox checkbox-primary">
          <input id="checkbox-load-check" class="styled" type="checkbox" v-model="permissionLoadCheck">
          <label for="checkbox-load-check">
            Load Check
          </label>
        </div>
        <div class="checkbox checkbox-primary">
          <input id="checkbox-invoices" class="styled" type="checkbox" v-model="permissionInvoices">
          <label for="checkbox-invoices">
            Invoices
          </label>
        </div>
        <div class="checkbox checkbox-primary">
          <input id="checkbox-payments" class="styled" type="checkbox" v-model="permissionPayments">
          <label for="checkbox-payments">
            Payments
          </label>
        </div>
        <div class="checkbox checkbox-primary">
          <input id="checkbox-reports" class="styled" type="checkbox" v-model="permissionReports">
          <label for="checkbox-reports">
            Reports
          </label>
        </div>
        <div class="checkbox checkbox-primary">
          <input id="checkbox-users" class="styled" type="checkbox" v-model="permissionUsers">
          <label for="checkbox-users">
            Users
          </label>
        </div>
        <div class="checkbox checkbox-primary">
          <input id="checkbox-system-settings" class="styled" type="checkbox" v-model="permissionSettings">
          <label for="checkbox-system-settings">
            System Settings
          </label>
        </div>
        <div class="checkbox checkbox-primary">
          <input id="checkbox-system-adminstrator" class="styled" type="checkbox" v-model="permissionAdministrator">
          <label for="checkbox-system-adminstrator">
            System Administrator
          </label>
        </div>
      </div>
    </div><!-- /.ibox -->

    <ladda-button :on-click="saveAccountUser" class="btn btn-success"><i class="fa fa-check"></i> Save</ladda-button>

    <div class="decorative-divider"></div>

    <div v-if="editingUser">
      <span v-if="!accountUser.driver_profile">
        <ladda-button
          button-type="button"
          v-if="accountUserDecorated.isEnabled"
          class="btn btn-sm m-b-sm btn-warning"
          :on-click="handleDisableUser">
          <i class="fa fa-ban"></i> Disable User
        </ladda-button>
        <ladda-button
          button-type="button"
          v-else
          class="btn btn-sm m-b-sm btn-success"
          :on-click="handleEnableUser">
          <i class="fa fa-refresh"></i> Enable User
        </ladda-button>
      </span>
      <ladda-button
        button-type="button"
        v-if="accountUserDecorated.isNotArchived"
        class="btn btn-sm m-b-sm btn-danger"
        :on-click="handleRemoveUser">
        <i class="fa fa-trash"></i> Delete User
      </ladda-button>
    </div>

    <div class="margin-50"></div>
  </div>
</template>

<script>
  import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
  import { SET_ACCOUNT_USER, SET_ACCOUNT_USER_ATTRIBUTE, RESET_ACCOUNT_USER } from 'vx/mutations'
  import { Selectize, LaddaButton, DateRange, PhotoCropper } from 'components/forms'
  import { has, get, merge, forIn, each } from 'lodash'
  import { existingUser, stateMapper } from 'mixins/user'
  import UserDetails from './../UserDetails'
  import notify from 'utils/notify'

  export default {
    mixins: [existingUser, stateMapper],

    data () {
      return {
        stateProperty: 'accountUser',
        mutationProperty: SET_ACCOUNT_USER_ATTRIBUTE,
        photo: null,
        originalPhotoDataUri: null,
        profile: null
      }
    },

    components: {
      Selectize,
      LaddaButton,
      DateRange,
      PhotoCropper,
      UserDetails
    },

    computed: {
      ...mapState('user/accountUsers', ['accountUser']),
      ...mapGetters('user/accountUsers', ['accountUserDecorated']),

      /**
       * Payload to send with request.
       *
       * @return object
       */
      payload () {
        return {
          name: this.name,
          email: this.email,
          mobile: this.mobile,
          role_id: this.roleId,
          licence: this.licence,
          licence_expiry: this.licenceExpiry,
          permissions: {
            load_check: this.permissionLoadCheck,
            invoices: this.permissionInvoices,
            payments: this.permissionPayments,
            reports: this.permissionReports,
            settings: this.permissionSettings,
            users: this.permissionUsers,
            administrator: this.permissionAdministrator
          }
        }
      },

      /**
       * Determine if we are editing a user.
       *
       * @return boolean
       */
      editingUser () {
        return has(this.$route.params, 'childId')
      },

      parentUserIsCarrier () {
        return this.user.isCarrier
      },

      editingOrCreateChildAdminAccount () {
        return this.user.isAdmin
      },

      loggedInUserIsAdmin () {
        return this.$user.isAdmin
      },

      /**
       * Get and set a users name on their profile.
       */
      name: {
        get () {
          return this.accountUser.profile.full_name
        },

        set (value) {
          this[SET_ACCOUNT_USER_ATTRIBUTE]({ attribute: 'profile.full_name', value })
        }
      },

      /**
       * Get and set account user load check permission.
       */
      permissionLoadCheck: {
        get () {
          return get(this.accountUser.permissions, 'load_check', false)
        },

        set (value) {
          this[SET_ACCOUNT_USER_ATTRIBUTE]({ attribute: 'permissions.load_check', value })
        }
      },

      /**
       * Get and set account user invoices permission.
       */
      permissionInvoices: {
        get () {
          return get(this.accountUser.permissions, 'invoices', false)
        },

        set (value) {
          this[SET_ACCOUNT_USER_ATTRIBUTE]({ attribute: 'permissions.invoices', value })
        }
      },

      /**
       * Get and set account user payments permission.
       */
      permissionPayments: {
        get () {
          return get(this.accountUser.permissions, 'payments', false)
        },

        set (value) {
          this[SET_ACCOUNT_USER_ATTRIBUTE]({ attribute: 'permissions.payments', value })
        }
      },

      /**
       * Get and set account user reports permission.
       */
      permissionReports: {
        get () {
          return get(this.accountUser.permissions, 'reports', false)
        },

        set (value) {
          this[SET_ACCOUNT_USER_ATTRIBUTE]({ attribute: 'permissions.reports', value })
        }
      },

      /**
       * Get and set account user settings permission.
       */
      permissionSettings: {
        get () {
          return get(this.accountUser.permissions, 'settings', false)
        },

        set (value) {
          this[SET_ACCOUNT_USER_ATTRIBUTE]({ attribute: 'permissions.settings', value })
        }
      },

      /**
       * Get and set account user users permission.
       */
      permissionUsers: {
        get () {
          return get(this.accountUser.permissions, 'users', false)
        },

        set (value) {
          this[SET_ACCOUNT_USER_ATTRIBUTE]({ attribute: 'permissions.users', value })
        }
      },

      /**
       * Get and set account user administrator permission.
       */
      permissionAdministrator: {
        get () {
          return get(this.accountUser.permissions, 'administrator', false)
        },

        set (value) {
          this[SET_ACCOUNT_USER_ATTRIBUTE]({ attribute: 'permissions.administrator', value })
        }
      }
    },

    created () {
      this.attemptToLoadExistingUser()
      this.attemptToLoadExistingAccountUser().then(() => {
        this.profile = Object.assign({}, this.accountUser.profile)
      })

      ;['notify_job_status', 'notify_load_deliveries', 'notify_job_completed', 'notify_public_jobs'].forEach(type => {
        this.$watch(`profile.${type}`, function (val) {
          this.updateUser({ id: this.profile.user_id, payload: { [type]: val } })
            .then(() => notify.success('Notification settings have been saved.'))
            .catch(() => (notify.error('Unable to save notification settings.')))
        })
      })
    },

    methods: {
      ...mapActions('user/accountUsers', ['getAccountUser', 'storeAccountUser', 'updateAccountUser', 'removeUser']),
      ...mapActions('driver', ['getDriverPhotoDataUri', 'uploadDriverPhoto']),
      ...mapActions('user', ['disableUser', 'enableUser', 'updateUser']),
      ...mapMutations('user/accountUsers', [SET_ACCOUNT_USER, RESET_ACCOUNT_USER, SET_ACCOUNT_USER_ATTRIBUTE]),

      /**
       * Attempt to load an existing user if we are editing someone.
       *
       * @return void
       */
      attemptToLoadExistingAccountUser () {
        this[RESET_ACCOUNT_USER]()

        if (this.editingUser) {
          const fetchOriginalPhotoDataUri = () => {
            if (this.accountUser.driver_id) {
              return this.getDriverPhotoDataUri({ id: this.accountUser.driver_id, dimension: 'original' })
                .then(response => {
                  this.originalPhotoDataUri = response.data.data_uri
                }).catch(error => {
                  if (get(error, 'response.statusCode') === 404) {
                    this.originalPhotoDataUri = null
                  }
                })
            }

            return Promise.resolve()
          }

          return this.getAccountUser(this.$route.params.childId)
            .then(fetchOriginalPhotoDataUri)
        }
      },

      saveAccountUser () {
        if (this.editingUser) {
          return this.validateAll()
            .then(() => {
              let payload = merge(this.payload, { parent_id: this.user.id })

              return this.updateAccountUser({ id: this.accountUser.id, payload })
                .then(this.handlePhotoUpload)
                .catch(exception => {
                  if (exception.response && exception.response.status === 422) {
                    forIn(exception.response.data.errors, (errors, key) => {
                      each(errors, error => {
                        this.$refs.userDetails.errors.add(key, error)
                        notify.error(error)
                      })
                    })
                  }
                })
            })
        } else {
          return this.validateAll()
            .then(() => {
              let payload = merge(this.payload, { parent_id: this.user.id })
              return this.storeAccountUser(payload)
                .then(this.updateAccountUserState)
                .then(this.handlePhotoUpload)
                .then(this.redirectToEditAccountUser)
                .catch(exception => {
                  if (exception.response && exception.response.status === 422) {
                    forIn(exception.response.data.errors, (errors, key) => {
                      each(errors, error => {
                        this.$refs.userDetails.errors.add(key, error)
                        notify.error(error)
                      })
                    })
                  }
                })
            })
        }
      },

      /**
       * Update an account user state after a saving action.
       *
       * @param object response
       *
       * @return Promise
       */
      updateAccountUserState (response) {
        this[SET_ACCOUNT_USER](response.data)

        return Promise.resolve(response)
      },

      /**
       * Handle disabling an account user.
       *
       * @return Promise
       */
      handleDisableUser () {
        return this.disableUser(this.accountUser.id)
          .then(this.updateAccountUserState)
      },

      /**
       * Handle enabling an account user.
       *
       * @return Promise
       */
      handleEnableUser () {
        return this.enableUser(this.accountUser.id)
          .then(this.updateAccountUserState)
      },

      /**
       * Handle archiving an account user.
       *
       * @return Promise
       */
      handleRemoveUser () {
        return this.removeUser({ id: this.accountUser.id, payload: { parent_id: this.user.id } })
          .then(this.redirectToList)
      },

      /**
       * Redirect to account users list.
       *
       * @param object response
       *
       * @return void
       */
      redirectToList (response) {
        if (this.editingDifferentUser) {
          this.$router.push({ name: 'users.account-users', params: { id: this.user.id } })
        } else {
          this.$router.push({ name: 'account.users' })
        }
      },

      /**
       * Redirect to edit account user.
       *
       * @param object response
       *
       * @return void
       */
      redirectToEditAccountUser (response) {
        if (this.editingDifferentUser) {
          this.$router.push({ name: 'account.parent.users.edit', params: { id: this.user.id, childId: this.accountUser.id } })
        } else {
          this.$router.push({ name: 'account.users.edit', params: { childId: this.accountUser.id } })
        }
      },

      /**
       * Handle uploading driver photo.
       *
       * @param object response
       *
       * @return Promise
       */
      handlePhotoUpload (response) {
        if (this.photo && response.data.driver_id) {
          return this.uploadDriverPhoto({ id: response.data.driver_id, file: this.photo.file, crop: this.photo.crop })
        }

        return Promise.resolve(response)
      }
    }
}
</script>




// WEBPACK FOOTER //
// AccountUserForm.vue?2f220499